/**
 * Projects stored in this browser's IndexedDB. One database holds every
 * project: a `projects` store with each project's name and dates, and a
 * `files` store keyed by [project id, path] holding each file's bytes. A
 * project's files are laid out as on disk: project.json at the top, and the
 * folder its `root` names below it.
 */
import type { Storage } from '@animo-editor/project';
import { folderFiles, FolderStorage, walk } from './folder.js';

export const DB_NAME = 'animo-editor';
const VERSION = 1;

export interface ProjectRecord {
  id: string;
  name: string;
  created: number;
  updated: number;
  /** When it was last exported as a .zip, if ever. */
  exported?: number;
  /**
   * The folder on the user's computer the project lives in, for projects
   * opened from a folder in Chrome and Edge. Their files are there, not in
   * this database.
   */
  folder?: FileSystemDirectoryHandle;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
  });
}

/** Every key of one project's files: [id, ''] up to [id, '￿']. */
const projectRange = (id: string) => IDBKeyRange.bound([id, ''], [id, '￿']);

export class ProjectDB {
  private constructor(private db: IDBDatabase) {}

  static open(name = DB_NAME): Promise<ProjectDB> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(name, VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('projects')) db.createObjectStore('projects', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('files')) db.createObjectStore('files');
      };
      req.onsuccess = () => resolve(new ProjectDB(req.result));
      req.onerror = () => reject(req.error);
    });
  }

  close(): void {
    this.db.close();
  }

  async projects(): Promise<ProjectRecord[]> {
    const tx = this.db.transaction('projects');
    const all = await request(tx.objectStore('projects').getAll() as IDBRequest<ProjectRecord[]>);
    return all.sort((a, b) => b.updated - a.updated);
  }

  project(id: string): Promise<ProjectRecord | undefined> {
    return request(this.db.transaction('projects').objectStore('projects').get(id) as IDBRequest<ProjectRecord | undefined>);
  }

  /** Adds a project with all its files in one transaction. */
  async create(name: string, files: Map<string, Uint8Array>): Promise<ProjectRecord> {
    const now = Date.now();
    const record: ProjectRecord = { id: crypto.randomUUID(), name, created: now, updated: now };
    const tx = this.db.transaction(['projects', 'files'], 'readwrite');
    tx.objectStore('projects').add(record);
    const store = tx.objectStore('files');
    for (const [path, bytes] of files) store.add(bytes, [record.id, path]);
    await done(tx);
    return record;
  }

  /** Adds a project that lives in a folder on the user's computer. */
  async createFolder(name: string, folder: FileSystemDirectoryHandle): Promise<ProjectRecord> {
    const now = Date.now();
    const record: ProjectRecord = { id: crypto.randomUUID(), name, created: now, updated: now, folder };
    const tx = this.db.transaction('projects', 'readwrite');
    tx.objectStore('projects').add(record);
    await done(tx);
    return record;
  }

  /** The stored project already linked to this folder, if any. */
  async findFolder(folder: FileSystemDirectoryHandle): Promise<ProjectRecord | undefined> {
    for (const record of await this.projects()) if (record.folder && (await record.folder.isSameEntry(folder))) return record;
    return undefined;
  }

  async update(id: string, patch: Partial<Omit<ProjectRecord, 'id'>>): Promise<void> {
    const tx = this.db.transaction('projects', 'readwrite');
    const store = tx.objectStore('projects');
    const record = (await request(store.get(id))) as ProjectRecord | undefined;
    if (record) store.put({ ...record, ...patch });
    await done(tx);
  }

  async remove(id: string): Promise<void> {
    const tx = this.db.transaction(['projects', 'files'], 'readwrite');
    tx.objectStore('projects').delete(id);
    tx.objectStore('files').delete(projectRange(id));
    await done(tx);
  }

  /** Every file of a project, for export and duplication. */
  async files(id: string): Promise<Map<string, Uint8Array>> {
    const folder = (await this.project(id))?.folder;
    if (folder) return folderFiles(folder);
    const tx = this.db.transaction('files');
    const store = tx.objectStore('files');
    const [keys, values] = await Promise.all([request(store.getAllKeys(projectRange(id))), request(store.getAll(projectRange(id)))]);
    const out = new Map<string, Uint8Array>();
    keys.forEach((k, i) => out.set((k as [string, string])[1], values[i] as Uint8Array));
    return out;
  }

  /** Every file's path in a project, without reading the files. A folder project's skip hidden files and node_modules. */
  async paths(id: string): Promise<string[]> {
    const folder = (await this.project(id))?.folder;
    if (folder) return (await walk(folder)).map((e) => e.path);
    const keys = await request(this.db.transaction('files').objectStore('files').getAllKeys(projectRange(id)));
    return keys.map((k) => (k as [string, string])[1]);
  }

  /** Total bytes of a project's files. */
  async size(id: string): Promise<number> {
    let total = 0;
    for (const bytes of (await this.files(id)).values()) total += bytes.byteLength;
    return total;
  }

  async read(id: string, path: string): Promise<Uint8Array | undefined> {
    const folder = (await this.project(id))?.folder;
    if (folder) return new FolderStorage(folder).bytes(path);
    return (await request(this.db.transaction('files').objectStore('files').get([id, path]))) as Uint8Array | undefined;
  }

  /**
   * Storage for one folder of a project: `prefix` is '' for the top, or the
   * root folder with a trailing slash. A folder project's storage is its folder.
   */
  storageOf(record: ProjectRecord, prefix = ''): Storage {
    if (record.folder) return new FolderStorage(record.folder, prefix, () => void this.update(record.id, { updated: Date.now() }));
    return this.storage(record.id, prefix);
  }

  /** Storage for one folder of a project in this database. */
  storage(id: string, prefix = ''): IdbStorage {
    return new IdbStorage(this.db, id, prefix, () => void this.update(id, { updated: Date.now() }));
  }
}

/** A project folder in IndexedDB, as the project model's Storage. */
export class IdbStorage implements Storage {
  readonly root: string;

  constructor(
    private db: IDBDatabase,
    private id: string,
    private prefix: string,
    private touched: () => void,
  ) {
    this.root = `indexeddb:${id}/${prefix}`;
  }

  private key(path: string): [string, string] {
    return [this.id, this.prefix + path];
  }

  async read(path: string): Promise<string | undefined> {
    const bytes = (await request(this.db.transaction('files').objectStore('files').get(this.key(path)))) as Uint8Array | undefined;
    return bytes === undefined ? undefined : decoder.decode(bytes);
  }

  async write(path: string, text: string): Promise<void> {
    const tx = this.db.transaction('files', 'readwrite');
    tx.objectStore('files').put(encoder.encode(text), this.key(path));
    await done(tx);
    this.touched();
  }

  async readBytes(path: string): Promise<Uint8Array | undefined> {
    return (await request(this.db.transaction('files').objectStore('files').get(this.key(path)))) as Uint8Array | undefined;
  }

  async replace(path: string, bytes: Uint8Array): Promise<void> {
    const tx = this.db.transaction('files', 'readwrite');
    tx.objectStore('files').put(bytes, this.key(path));
    await done(tx);
    this.touched();
  }

  async create(path: string, bytes: Uint8Array): Promise<void> {
    const tx = this.db.transaction('files', 'readwrite');
    // add, unlike put, fails when the key exists, so nothing is replaced.
    tx.objectStore('files').add(bytes, this.key(path));
    try {
      await done(tx);
    } catch (e) {
      throw new Error(`${path} already exists`, { cause: e });
    }
    this.touched();
  }

  async exists(path: string): Promise<boolean> {
    return (await request(this.db.transaction('files').objectStore('files').count(this.key(path)))) > 0;
  }

  async remove(path: string): Promise<void> {
    const tx = this.db.transaction('files', 'readwrite');
    tx.objectStore('files').delete(this.key(path));
    await done(tx);
    this.touched();
  }

  async move(from: string, to: string): Promise<void> {
    const tx = this.db.transaction('files', 'readwrite');
    const files = tx.objectStore('files');
    const get = files.get(this.key(from));
    get.onsuccess = () => {
      if (get.result === undefined) return tx.abort();
      // add fails when the key exists, which aborts the whole move.
      files.add(get.result, this.key(to));
      files.delete(this.key(from));
    };
    try {
      await done(tx);
    } catch (e) {
      throw new Error(get.result === undefined ? `${from} does not exist` : `${to} already exists`, { cause: e });
    }
    this.touched();
  }

  async list(filter: (path: string) => boolean): Promise<string[]> {
    const keys = await request(this.db.transaction('files').objectStore('files').getAllKeys(projectRange(this.id)));
    const out: string[] = [];
    for (const k of keys) {
      const path = (k as [string, string])[1];
      if (!path.startsWith(this.prefix)) continue;
      const rel = path.slice(this.prefix.length);
      // Hidden files and folders are skipped, as on disk.
      if (rel.split('/').some((part) => part.startsWith('.') || part === 'node_modules')) continue;
      if (filter(rel)) out.push(rel);
    }
    return out.sort();
  }
}
